import React, { useMemo } from "react";
import { Box, List, ListItemButton, ListItemText, Paper, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  type ChatOutputType,
  type ChatSlashCommand,
  type WidgetFormatOption,
  filterSlashCommands,
  filterWidgetFormats,
  chartTypeLabel,
} from "../../utils/chatSlashCommands";
import { WIDGET_CHART_TYPES } from "../../utils/chartTypeRegistry";

export type SlashPickerMode = "commands" | "widgetFormats" | "chartTypes";

type Props = {
  open: boolean;
  mode: SlashPickerMode;
  query: string;
  forOutputType?: ChatOutputType;
  onSelectCommand: (command: ChatSlashCommand) => void;
  onSelectWidgetFormat: (format: WidgetFormatOption) => void;
  onSelectChartType: (chartType: string) => void;
};

export default function ChatSlashCommandPicker({
  open,
  mode,
  query,
  forOutputType,
  onSelectCommand,
  onSelectWidgetFormat,
  onSelectChartType,
}: Props) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;

  const commands = useMemo(() => filterSlashCommands(query), [query]);
  const widgetFormats = useMemo(() => filterWidgetFormats(query), [query]);

  const chartTypes = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return WIDGET_CHART_TYPES;
    return WIDGET_CHART_TYPES.filter(
      (c) =>
        c.id.toLowerCase().includes(q) ||
        c.labelEn.toLowerCase().includes(q) ||
        c.labelTr.toLowerCase().includes(q)
    );
  }, [query]);

  if (!open) return null;

  return (
    <Paper
      elevation={4}
      sx={{
        position: "absolute",
        bottom: "100%",
        left: 0,
        right: 0,
        mb: 1,
        maxHeight: 280,
        overflow: "auto",
        zIndex: 10,
      }}
    >
      {mode === "commands" && (
        <>
          <Box sx={{ px: 1.5, pt: 1, pb: 0.5 }}>
            <Typography variant="caption" color="text.secondary" display="block">
              {t("chat.slash.title")}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ opacity: 0.85 }}>
              {t("chat.tooltips.slash")}
            </Typography>
          </Box>
          <List dense disablePadding>
            {commands.map((cmd) => (
              <ListItemButton key={cmd.id} onClick={() => onSelectCommand(cmd)}>
                <ListItemText
                  primary={t(cmd.labelKey)}
                  secondary={t(cmd.descriptionKey)}
                  primaryTypographyProps={{ variant: "body2", fontWeight: 600 }}
                  secondaryTypographyProps={{ variant: "caption" }}
                />
              </ListItemButton>
            ))}
            {commands.length === 0 && (
              <Box sx={{ px: 2, py: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  {t("chat.slash.noMatch")}
                </Typography>
              </Box>
            )}
          </List>
        </>
      )}

      {mode === "widgetFormats" && (
        <>
          <Box sx={{ px: 1.5, pt: 1, pb: 0.5 }}>
            <Typography variant="caption" color="text.secondary">
              {t("chat.slash.pickWidgetFormat")}
            </Typography>
          </Box>
          <List dense disablePadding>
            {widgetFormats.map((opt) => (
              <ListItemButton key={opt.id} onClick={() => onSelectWidgetFormat(opt)}>
                <ListItemText
                  primary={t(opt.labelKey)}
                  secondary={t(opt.descriptionKey)}
                  primaryTypographyProps={{ variant: "body2", fontWeight: 600 }}
                  secondaryTypographyProps={{ variant: "caption" }}
                />
              </ListItemButton>
            ))}
          </List>
        </>
      )}

      {mode === "chartTypes" && (
        <>
          <Box sx={{ px: 1.5, pt: 1, pb: 0.5 }}>
            <Typography variant="caption" color="text.secondary">
              {forOutputType === "widget"
                ? t("chat.slash.pickWidgetChartType")
                : t("chat.slash.pickChartType")}
            </Typography>
          </Box>
          <List dense disablePadding>
            {chartTypes.map((ct) => (
              <ListItemButton key={ct.id} onClick={() => onSelectChartType(ct.id)}>
                <ListItemText
                  primary={chartTypeLabel(ct.id, t, locale)}
                  secondary={ct.id}
                  primaryTypographyProps={{ variant: "body2" }}
                  secondaryTypographyProps={{ variant: "caption" }}
                />
              </ListItemButton>
            ))}
          </List>
        </>
      )}
    </Paper>
  );
}
